import { Alert, AlertDescription, AlertTitle } from "@car/ui/components/alert";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@car/ui/components/card";
import { Image } from "@car/ui/components/image";
import { Progress } from "@car/ui/components/progress";
import { Skeleton } from "@car/ui/components/skeleton";
import {
  Alert02Icon,
  CalendarAdd01Icon,
  Cancel01Icon,
  Car03Icon,
  CheckmarkCircle02Icon,
  ContractsIcon,
  DeliveryTruck01Icon,
  GaugeIcon,
  Medal01Icon,
  RepairIcon,
  SignatureIcon,
  Wallet01Icon,
  Wrench01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import {
  NoVehicleEmpty,
  PageHeader,
  PanelQueryError,
  Plate,
  RepairStatusBadge,
  Stat,
  WarrantyBadge,
} from "@/components/panel-bits";
import { faDate, faKm, faNumber, faPlainNumber, faToman } from "@/lib/format";
import { byDueUrgency, garageName, orderStageLabels, serviceTypeLabels } from "@/lib/panel";
import { orpc } from "@/utils/orpc";

import { SEP } from "@car/ui/lib/utils";

export const Route = createFileRoute("/panel/")({
  head: () => ({ meta: [{ title: "داشبورد | پنل مشتریان تابان خودرو" }] }),
  component: PanelDashboard,
});

function PanelDashboard() {
  const query = useQuery(orpc.panel.overview.queryOptions());
  // wallet balance, and a club standing that respects a tier staff granted by hand
  const clubQuery = useQuery(orpc.panel.club.summary.queryOptions());
  // a test drive booked on the website under this phone: it is a sales booking, not a service visit
  const testDriveQuery = useQuery(orpc.panel.appointments.testDrives.queryOptions());

  if (query.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-9 w-64" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (query.isError) {
    return <PanelQueryError title="داشبورد بارگیری نشد" query={query} />;
  }

  const {
    profile,
    garage,
    orders,
    appointments,
    repairOrders,
    notifications,
    latestPoints,
    pendingContracts,
    hasServiceHistory,
  } = query.data;
  const club = clubQuery.data?.club ?? query.data.club;
  const wallet = clubQuery.data?.wallet ?? null;
  // contracts waiting for a signature get their own banner above the other alerts
  const otherAlerts = notifications.filter((item) => item.kind !== "contract");
  const topAlerts = otherAlerts.slice(0, 5);
  const moreAlerts = otherAlerts.length - topAlerts.length;
  const nextAppointment = appointments[0] ?? null;
  const nextTestDrive =
    testDriveQuery.data?.testDrives.find(
      (drive) => drive.status === "scheduled" && new Date(drive.scheduledIso) > new Date(),
    ) ?? null;
  const activeRepair = repairOrders[0] ?? null;
  // نوبت سرویس برای یک خودرو گرفته می‌شود: بدون خودرو دکمه‌ی رزرو به صفحه‌ی خالی می‌رسد
  const hasVehicle = garage.length > 0;
  const soonestDue = garage
    .flatMap((item) => item.maintenance.map((due) => ({ due, garage: item })))
    .filter(({ due }) => due.status !== "ok")
    .sort((a, b) => byDueUrgency(a.due, b.due))[0];

  return (
    <div className="grid gap-6">
      <PageHeader
        title={profile.firstName ? `سلام، ${profile.firstName} عزیز` : "سلام، خوش آمدید"}
        description="وضعیت خودروها، سرویس‌ها و امتیاز باشگاه شما در یک نگاه."
        action={
          hasVehicle ? (
            <Button nativeButton={false} render={<Link to="/panel/appointments/new" />}>
              <HugeiconsIcon icon={CalendarAdd01Icon} strokeWidth={1.8} />
              رزرو نوبت جدید
            </Button>
          ) : undefined
        }
      />

      <div
        className={
          hasVehicle ? "grid gap-4 sm:grid-cols-2 lg:grid-cols-4" : "grid gap-4 sm:grid-cols-2"
        }
      >
        {hasVehicle ? (
          <Stat
            icon={Car03Icon}
            label="خودروهای تحت مالکیت"
            value={`${faNumber(garage.length)} خودرو`}
            hint="با مشخصات کامل و سوابق خدمات"
          />
        ) : null}
        <Stat
          icon={Medal01Icon}
          label={`سطح ${club.tier.label}`}
          value={`${faNumber(club.balance)} امتیاز قابل استفاده`}
          hint={
            club.nextTier
              ? `${faNumber(club.remainingToNext)} امتیاز تا سطح ${club.nextTier.label}`
              : "بالاترین سطح باشگاه"
          }
        />
        <Stat
          icon={CalendarAdd01Icon}
          label="نوبت بعدی"
          value={
            nextAppointment
              ? faDate(nextAppointment.dateIso)
              : nextTestDrive
                ? faDate(nextTestDrive.scheduledIso)
                : "نوبتی رزرو نشده"
          }
          hint={
            nextAppointment
              ? `${serviceTypeLabels[nextAppointment.serviceType]}، ساعت ${nextAppointment.slot}`
              : nextTestDrive
                ? `تست‌درایو ${nextTestDrive.vehicleName}${SEP}${nextTestDrive.branchName}`
                : hasVehicle
                  ? "از دکمه رزرو نوبت سرویس نوبت بگیرید"
                  : "بعد از ثبت خودرو می‌توانید نوبت سرویس بگیرید"
          }
        />
        {hasVehicle ? (
          <Stat
            icon={Wrench01Icon}
            label="نزدیک‌ترین سرویس"
            value={soonestDue ? soonestDue.due.item.title : "همه‌چیز به‌روز است"}
            hint={
              soonestDue
                ? `${soonestDue.garage.vehicle.name}: ${soonestDue.due.text}`
                : "بر اساس برنامه سرویس خودروهای شما"
            }
          />
        ) : null}
      </div>

      {pendingContracts > 0 ? (
        <Alert className="border-amber-500/40 bg-amber-500/5">
          <HugeiconsIcon icon={ContractsIcon} strokeWidth={1.8} />
          <AlertTitle>
            {pendingContracts === 1
              ? "یک قرارداد منتظر امضای شماست"
              : `${faNumber(pendingContracts)} قرارداد منتظر امضای شماست`}
          </AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            متن را بخوانید و پیش از پایان مهلت، قرارداد را امضا یا رد کنید.
            <Button size="sm" nativeButton={false} render={<Link to="/panel/contracts" />}>
              <HugeiconsIcon icon={SignatureIcon} strokeWidth={1.8} />
              مشاهده و امضا
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {topAlerts.length > 0 ? (
        <section className="grid gap-3" aria-label="یادآوری‌ها و اعلان‌ها">
          {topAlerts.map((notification) => (
            <Alert
              key={notification.id}
              variant={notification.severity === "warning" ? "destructive" : "default"}
            >
              <HugeiconsIcon
                icon={notification.kind === "survey" ? Medal01Icon : Alert02Icon}
                strokeWidth={1.8}
              />
              <AlertTitle>{notification.title}</AlertTitle>
              <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
                {notification.description}
                {notification.ownedVehicleId !== null ? (
                  <Link
                    to="/panel/vehicles/$ownedId"
                    params={{ ownedId: notification.ownedVehicleId }}
                    className="font-bold underline underline-offset-4"
                  >
                    جزئیات خودرو
                  </Link>
                ) : (
                  <Link to={notification.to} className="font-bold underline underline-offset-4">
                    مشاهده و اقدام
                  </Link>
                )}
              </AlertDescription>
            </Alert>
          ))}
          {moreAlerts > 0 ? (
            <p className="text-sm text-muted-foreground">
              {faNumber(moreAlerts)} اعلان دیگر در مرکز اعلان‌ها (زنگوله بالای صفحه) در انتظار شماست.
            </p>
          ) : null}
        </section>
      ) : null}

      {orders.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HugeiconsIcon icon={DeliveryTruck01Icon} strokeWidth={1.8} className="size-5" />
              سفارش‌های در راه
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            {orders.map((order) => (
              <div key={order.id} className="grid gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="font-bold">{order.vehicle.name}</span>
                  <Badge
                    className={
                      order.hold
                        ? "bg-amber-500/15 text-amber-800 dark:text-amber-400"
                        : "bg-primary/10 text-primary"
                    }
                  >
                    {order.hold ? "توقف موقت فرآیند" : orderStageLabels[order.stage]}
                  </Badge>
                </div>
                <Progress
                  value={Math.round(order.progress * 100)}
                  aria-label={`پیشرفت سفارش ${order.vehicle.name}`}
                />
                <p className="text-sm text-muted-foreground">
                  {[order.location, `تحویل تقریبی ${faDate(order.estimatedDeliveryIso)}`]
                    .filter(Boolean)
                    .join(SEP)}
                </p>
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              className="justify-self-start"
              nativeButton={false}
              render={<Link to="/panel/orders" />}
            >
              پیگیری کامل سفارش‌ها
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* پایین‌تر از سفارش‌های در راه: مشتری تازه نخست باید وضعیت خریدش را ببیند، نه راهنما را */}
      <FirstSteps
        steps={[
          {
            label: "نام و کد ملی خود را کامل کنید",
            hint: "برای صدور فاکتور و پرونده خودرو لازم است.",
            action: "تکمیل پروفایل",
            to: "/panel/profile",
            done: Boolean(profile.firstName && profile.lastName && profile.nationalId),
          },
          {
            label: "خودروی خود را در پنل ببینید",
            hint: "اگر خودرویتان در فهرست نیست، درخواست افزودن بدهید.",
            action: "دیدن خودروها",
            to: "/panel/vehicles",
            done: hasVehicle,
          },
          {
            label: "اولین نوبت سرویس را رزرو کنید",
            hint: "روز و ساعت مراجعه به شعبه را خودتان انتخاب کنید.",
            action: "رزرو نوبت",
            to: "/panel/appointments/new",
            done: hasServiceHistory || appointments.length > 0 || repairOrders.length > 0,
            blockedHint: hasVehicle ? undefined : "بعد از ثبت خودرو",
          },
        ]}
      />

      <section className="grid gap-4 lg:grid-cols-2" aria-label="خودروهای من">
        {garage.length === 0 ? (
          <div className="lg:col-span-2">
            <NoVehicleEmpty />
          </div>
        ) : null}
        {garage.map((item) => (
          <Card key={item.owned.id} className={item.vehicle.image ? "overflow-hidden pt-0" : ""}>
            {item.vehicle.image ? (
              <Image
                src={item.vehicle.image}
                alt={item.vehicle.name}
                width={960}
                height={240}
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="h-40 w-full object-cover"
              />
            ) : null}
            <CardHeader className="pb-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">
                  {garageName(item)}{" "}
                  <span className="font-normal text-muted-foreground">
                    مدل {faPlainNumber(item.owned.modelYear)}
                  </span>
                </CardTitle>
                <WarrantyBadge warranty={item.warranty} />
              </div>
            </CardHeader>
            <CardContent className="grid gap-3">
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <Plate plate={item.owned.plate} text={item.owned.plateText} />
                <span className="inline-flex items-center gap-1.5">
                  <HugeiconsIcon icon={GaugeIcon} strokeWidth={1.8} className="size-4" />
                  کارکرد تخمینی {faKm(item.estimatedKm)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{item.estimateNote}</p>
              {item.nextDue ? (
                <p className="text-sm text-muted-foreground">
                  سرویس بعدی:{" "}
                  <span className="font-bold text-foreground">{item.nextDue.item.title}</span>،{" "}
                  {item.nextDue.text}
                </p>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="justify-self-start"
                nativeButton={false}
                render={<Link to="/panel/vehicles/$ownedId" params={{ ownedId: item.owned.id }} />}
              >
                مشاهده جزئیات و سوابق
              </Button>
            </CardContent>
          </Card>
        ))}
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HugeiconsIcon icon={RepairIcon} strokeWidth={1.8} className="size-5" />
              خودرو در تعمیرگاه
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeRepair ? (
              <div className="grid gap-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-bold">{activeRepair.title}</span>
                  <RepairStatusBadge status={activeRepair.status} />
                </div>
                <p className="text-muted-foreground">{activeRepair.note}</p>
                <p className="text-muted-foreground">
                  شماره پذیرش {faPlainNumber(activeRepair.number)}
                  {SEP}
                  {activeRepair.branchName}
                  {activeRepair.estimatedReadyIso
                    ? `${SEP}تحویل تقریبی ${faDate(activeRepair.estimatedReadyIso)}`
                    : ""}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-self-start"
                  nativeButton={false}
                  render={<Link to="/panel/appointments" />}
                >
                  پیگیری وضعیت
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">هیچ خودرویی از شما در تعمیرگاه نیست.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <HugeiconsIcon icon={Medal01Icon} strokeWidth={1.8} className="size-5" />
              باشگاه مشتریان
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="flex items-center justify-between text-sm">
              <Badge variant="secondary">سطح {club.tier.label}</Badge>
              {club.nextTier ? (
                <span className="text-muted-foreground">
                  {faNumber(club.remainingToNext)} امتیاز تا {club.nextTier.label}
                </span>
              ) : null}
            </div>
            <Progress
              value={Math.round(club.progressToNext * 100)}
              aria-label="پیشرفت تا سطح بعدی"
            />
            {wallet ? (
              <Link
                to="/panel/club"
                className="flex items-center justify-between gap-2 rounded-lg bg-muted/60 px-3 py-2 text-sm hover:bg-muted"
              >
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <HugeiconsIcon icon={Wallet01Icon} strokeWidth={1.8} className="size-4" />
                  موجودی کیف پول
                </span>
                <span className="font-black">{faToman(wallet.balance)}</span>
              </Link>
            ) : null}
            <ul className="grid gap-1.5 text-sm text-muted-foreground">
              {latestPoints.map((entry) => (
                <li key={entry.id} className="flex items-center justify-between gap-2">
                  <span>{entry.title}</span>
                  <span
                    className={
                      entry.points > 0
                        ? "font-bold text-emerald-700 dark:text-emerald-400"
                        : "font-bold"
                    }
                  >
                    {entry.points > 0
                      ? `${faNumber(entry.points)}+`
                      : `${faNumber(-entry.points)}−`}
                  </span>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              size="sm"
              className="justify-self-start"
              nativeButton={false}
              render={<Link to="/panel/club" />}
            >
              مشاهده باشگاه و جوایز
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

type FirstStep = {
  label: string;
  hint: string;
  /** برچسب دکمه؛ هر قدم فعل خودش را دارد تا دکمه‌ها شبیه هم نباشند */
  action: string;
  to: "/panel/profile" | "/panel/vehicles" | "/panel/appointments/new";
  done: boolean;
  /** تا این قدم شدنی نیست: به‌جای دکمه همین متن می‌آید */
  blockedHint?: string;
};

const FIRST_STEPS_KEY = "panel-first-steps-closed";

const readClosed = () => {
  try {
    return localStorage.getItem(FIRST_STEPS_KEY) === "1";
  } catch {
    return false;
  }
};

/** راهنمای شروع برای مشتری تازه؛ با انجام همه قدم‌ها یا بستن آن دیگر دیده نمی‌شود */
function FirstSteps({ steps }: { steps: FirstStep[] }) {
  const [closed, setClosed] = useState(readClosed);
  const doneCount = steps.filter((step) => step.done).length;
  if (closed || doneCount === steps.length) return null;

  const close = () => {
    setClosed(true);
    try {
      localStorage.setItem(FIRST_STEPS_KEY, "1");
    } catch {
      // بدون حافظه مرورگر فقط در همین بازدید بسته می‌ماند
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">شروع کار با پنل</CardTitle>
        <CardDescription>
          {faNumber(doneCount)} از {faNumber(steps.length)} قدم انجام شده است.
        </CardDescription>
        <CardAction>
          <Button variant="ghost" size="icon-sm" aria-label="بستن راهنمای شروع" onClick={close}>
            <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} />
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <ol className="grid gap-2">
          {steps.map((step, index) => (
            <li
              key={step.to}
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted/50 p-3"
            >
              <span className="flex items-start gap-2.5">
                {step.done ? (
                  <HugeiconsIcon
                    icon={CheckmarkCircle02Icon}
                    strokeWidth={1.8}
                    className="mt-0.5 size-5 shrink-0 text-emerald-600 dark:text-emerald-400"
                  />
                ) : (
                  <span
                    aria-hidden
                    className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border text-xs font-bold"
                  >
                    {faNumber(index + 1)}
                  </span>
                )}
                <span className="grid gap-0.5">
                  <span
                    className={
                      step.done ? "text-sm text-muted-foreground line-through" : "text-sm font-bold"
                    }
                  >
                    {step.label}
                    {step.done ? <span className="sr-only"> (انجام شد)</span> : null}
                  </span>
                  {step.done ? null : (
                    <span className="text-xs text-muted-foreground">{step.hint}</span>
                  )}
                </span>
              </span>
              {step.done ? null : step.blockedHint ? (
                <span className="text-xs text-muted-foreground">{step.blockedHint}</span>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  nativeButton={false}
                  render={<Link to={step.to} />}
                >
                  {step.action}
                </Button>
              )}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
